/**
 * SettingsPage — Phase 9d (2026-06-18).
 *
 * Every authenticated user gets a "My Account → Change password" card.
 * The admin-only Reset All System Data section has been retired per user
 * request. The backend endpoint `POST /api/admin/reset-data` is preserved
 * for ops/CLI use only.
 */
import { useState, useEffect } from "react";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Eye, EyeOff, CheckCircle2, XCircle, KeyRound, Link as LinkIcon, AlertTriangle, Trash2, Palette, Monitor, Sun, Moon, Sparkles, Contrast as ContrastIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAppearance } from "@/theme/ThemeProvider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const PW_SPECIAL = "!@#$%^&*()_+-=[]{}|;:,.<>?";

function evaluatePolicy(newPw, currentPw, email) {
  const local = (email || "").split("@", 1)[0].toLowerCase();
  return [
    { rule: "length", label: "At least 10 characters", ok: newPw.length >= 10 },
    { rule: "uppercase", label: "Includes an uppercase letter", ok: /[A-Z]/.test(newPw) },
    { rule: "lowercase", label: "Includes a lowercase letter", ok: /[a-z]/.test(newPw) },
    { rule: "digit", label: "Includes a digit", ok: /[0-9]/.test(newPw) },
    {
      rule: "special",
      label: "Includes a special character (!@#$%^&* …)",
      ok: [...newPw].some((c) => PW_SPECIAL.includes(c)),
    },
    {
      rule: "different",
      label: "Different from your current password",
      ok: newPw.length > 0 && newPw !== currentPw,
    },
    {
      rule: "no_email_local",
      label: "Does not contain your email username",
      ok: newPw.length === 0 || local.length < 3 || !newPw.toLowerCase().includes(local),
    },
  ];
}

export default function SettingsPage() {
  const { user } = useAuth();
  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
          <CardDescription>
            Signed in as <span className="font-medium">{user?.email}</span>{" "}
            ({user?.role?.replace(/_/g, " ")}).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600">
            Manage your account credentials below. Company-wide tax brackets are
            managed in{" "}
            <Link to="/settings/taxes" className="text-[#2F86B5] underline inline-flex items-center gap-1">
              <LinkIcon className="h-3 w-3" /> Tax Settings
            </Link>
            .
          </p>
        </CardContent>
      </Card>

      <ChangePasswordCard userEmail={user?.email} />

      {/* Phase 43 (2026-07-28) - Theme / accent / font-size / contrast settings.
          Every user (any role) has this card. */}
      <AppearanceSettingsCard />

      {(user?.role === "system_admin" || user?.full_access === true) && <AutoHealSettingsCard />}

      {/* TEMPORARY — remove after user finishes initial production cleanup.
          Renders only for system_admin. Calls POST /api/admin/reset-data with
          skip_demo_seed:true so masters (users, company, CoA, tax_settings)
          are preserved and no demo data is reseeded. */}
      {(user?.role === "system_admin" || user?.full_access === true) && <TemporaryWipeDemoDataCard />}
    </div>
  );
}

/**
 * Phase 41 (2026-07-28) — Feature flag + kill-switch settings for the
 * autonomous Data Integrity Auto-Heal engine.
 */
function AutoHealSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/auto-heal/settings");
      setSettings(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Auto-Heal settings");
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.patch("/admin/auto-heal/settings",
        { auto_heal_enabled: !settings.auto_heal_enabled });
      toast.success(`Auto-Heal ${!settings.auto_heal_enabled ? "enabled" : "disabled"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  return (
    <Card id="auto-heal" data-testid="auto-heal-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Data Integrity Auto-Heal
        </CardTitle>
        <CardDescription>
          Every 15 minutes the system scans the ledger for known data-integrity issues
          (OYAK duplicates, wrong-sign opening balances, orphan reversals, negative cash
          accounts, …) and safely repairs them via reversing JEs. Ambiguous items go to
          the review queue rather than being auto-repaired.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {settings ? (
          <div className="flex items-start gap-4">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="auto-heal-toggle">
              <input
                type="checkbox"
                checked={!!settings.auto_heal_enabled}
                onChange={toggle}
                disabled={saving}
                className="w-4 h-4 accent-emerald-600"
              />
              <span className="text-sm font-medium">
                Auto-Heal is{" "}
                <span className={settings.auto_heal_enabled ? "text-emerald-700" : "text-red-700"}>
                  {settings.auto_heal_enabled ? "ENABLED" : "DISABLED"}
                </span>
              </span>
            </label>
            <div className="text-xs text-slate-600 space-y-1">
              <p>Interval: every {settings.interval_minutes} minutes</p>
              <p>Global kill-switch: aborts if a single run would post more than {settings.global_kill_switch_reversals} reversal JEs.</p>
              <p>Per-rule kill-switches: {Object.entries(settings.per_rule_kill_switch || {}).map(([k, v]) => `${k}=${v}`).join(", ")}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Loading…</p>
        )}
        <div className="flex gap-2 pt-2">
          <Link to="/admin/auto-heal-log">
            <Button variant="outline" size="sm" data-testid="link-view-log">View Log</Button>
          </Link>
          <Link to="/admin/review-queue">
            <Button variant="outline" size="sm" data-testid="link-view-queue">Review Queue</Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}


/** TEMPORARY — Phase 9d cleanup tool. Remove this card once the user has
 *  wiped production demo data. */
function TemporaryWipeDemoDataCard() {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const pwRef = useState(null)[0]; // simple inline ref

  const submit = async () => {
    if (pw.length < 1 || busy) return;
    setBusy(true);
    try {
      await api.post("/admin/reset-data", { password: pw, skip_demo_seed: true });
      toast.success("All demo data erased. System is now empty and ready for real use.");
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      const msg = e?.response?.status === 401 ? "Incorrect password" : (e?.response?.data?.detail || "Wipe failed");
      toast.error(typeof msg === "string" ? msg : "Wipe failed");
      setBusy(false);
    }
  };

  return (
    <Card className="border-2 border-amber-300 bg-amber-50/40" data-testid="temp-wipe-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-amber-800">
          <AlertTriangle className="h-4 w-4" /> Temporary — Wipe demo data
        </CardTitle>
        <CardDescription className="text-xs text-amber-800/90">
          One-time admin tool to erase all sample transactions, payrolls,
          suppliers, customers, employees, compliance items, tasks, approvals,
          documents, loans, JEs, audit logs, and notifications — leaving only
          the system shell (users, company, Chart of Accounts, tax settings)
          ready for real business data entry. This card will be removed once
          the production cleanup is finalized.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="outline"
          onClick={() => setOpen(true)}
          className="border-amber-400 text-amber-900 hover:bg-amber-100"
          data-testid="temp-wipe-open-button"
        >
          <Trash2 className="h-4 w-4 mr-2" /> Wipe demo data
        </Button>
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (!v) setPw(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="temp-wipe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" />
              Wipe demo data
            </DialogTitle>
            <DialogDescription>
              This will permanently erase ALL transactional records. Users,
              company profile, Chart of Accounts and tax settings will be
              preserved. Re-enter your admin password to confirm.
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Cannot be undone. After wipe, the system will be empty and ready
              for real business data entry.
            </AlertDescription>
          </Alert>
          <Input
            ref={pwRef}
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="Your admin password"
            autoComplete="current-password"
            autoFocus
            className="mt-2"
            data-testid="temp-wipe-password-input"
          />
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => { setOpen(false); setPw(""); }} disabled={busy}
                    data-testid="temp-wipe-cancel-button">
              Cancel
            </Button>
            <Button variant="destructive" onClick={submit} disabled={busy || pw.length < 1}
                    data-testid="temp-wipe-confirm-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Trash2 className="mr-2 h-4 w-4" /> Wipe everything now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PwInput({ value, onChange, placeholder, testId, onKeyDown }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="new-password"
        className="h-10 pr-10"
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-700"
        aria-label={show ? "Hide password" : "Show password"}
        data-testid={`${testId}-toggle`}
        tabIndex={-1}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function ChangePasswordCard({ userEmail }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const policy = evaluatePolicy(next, current, userEmail);
  const policyOk = policy.every((p) => p.ok);
  const matchesConfirm = next.length > 0 && next === confirm;
  const canSubmit = current.length > 0 && policyOk && matchesConfirm && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated successfully");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) {
        toast.error("Current password is incorrect");
      } else if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        toast.error(detail.errors.map((x) => x.message).join(" · "));
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not update password");
      }
    } finally {
      setBusy(false);
    }
  };

  const onEnter = (e) => {
    if (e.key === "Enter" && canSubmit) submit();
  };

  return (
    <Card data-testid="change-password-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4 text-[#2F86B5]" />
          Change password
        </CardTitle>
        <CardDescription className="text-xs">
          Update the password for{" "}
          <span className="font-mono text-[#2F86B5]">{userEmail || "your account"}</span>.
          You will stay signed in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="cur-pw" className="text-sm">Current password</Label>
          <PwInput
            value={current}
            onChange={setCurrent}
            placeholder="Your current password"
            testId="cur-pw-input"
            onKeyDown={onEnter}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="new-pw" className="text-sm">New password</Label>
          <PwInput
            value={next}
            onChange={setNext}
            placeholder="At least 10 characters with mixed case, digits & symbol"
            testId="new-pw-input"
            onKeyDown={onEnter}
          />
          <ul className="mt-2 space-y-1" data-testid="pw-policy-list">
            {policy.map((p) => (
              <li
                key={p.rule}
                className={`flex items-center gap-1.5 text-xs ${p.ok ? "text-emerald-600" : "text-slate-500"}`}
                data-testid={`pw-rule-${p.rule}-${p.ok ? "ok" : "fail"}`}
              >
                {p.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                {p.label}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm-pw" className="text-sm">Confirm new password</Label>
          <PwInput
            value={confirm}
            onChange={setConfirm}
            placeholder="Re-enter the new password"
            testId="confirm-pw-input"
            onKeyDown={onEnter}
          />
          {confirm.length > 0 && !matchesConfirm && (
            <p className="flex items-center gap-1 text-xs text-rose-600" data-testid="confirm-mismatch">
              <XCircle className="h-3.5 w-3.5" /> Passwords don&apos;t match
            </p>
          )}
        </div>

        {!policyOk && next.length > 0 && (
          <Alert variant="destructive" className="py-2">
            <AlertDescription className="text-xs">
              New password doesn&apos;t meet policy yet. Tick all green check marks above before submitting.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={() => { setCurrent(""); setNext(""); setConfirm(""); }}
            disabled={busy}
            data-testid="change-pw-reset-button"
          >
            Clear
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
            data-testid="change-pw-submit-button"
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <KeyRound className="mr-2 h-4 w-4" /> Change password
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Phase 43 (2026-07-28) - Appearance settings card.
 * Live-preview on state change; autosaves (debounced 400ms) via ThemeProvider.
 */
const THEME_OPTIONS = [
  { value: "system",         label: "System Default", desc: "Follows OS setting",     Icon: Monitor,  swatch: "bg-slate-100 border-slate-300" },
  { value: "light",          label: "Light",           desc: "Modern SaaS Cockpit",   Icon: Sun,      swatch: "bg-white border-slate-300" },
  { value: "dark",           label: "Dark",            desc: "Deep navy background",   Icon: Moon,     swatch: "bg-[#04122B] border-[#12294B]" },
  { value: "eye_comfort",    label: "Eye Comfort",     desc: "Warm sepia, softer",     Icon: Sparkles, swatch: "bg-[#F5EEDF] border-[#C9BB9C]" },
  { value: "high_contrast",  label: "High Contrast",   desc: "WCAG AAA, thick borders", Icon: ContrastIcon, swatch: "bg-white border-black border-2" },
];

const ACCENT_OPTIONS = [
  { value: "blue",   hex: "#1F4FA3", label: "Blue" },
  { value: "navy",   hex: "#0F1E3D", label: "Navy" },
  { value: "green",  hex: "#0E9F6E", label: "Green" },
  { value: "grey",   hex: "#475569", label: "Grey" },
  { value: "purple", hex: "#7C3AED", label: "Purple" },
];

const FONT_OPTIONS = [
  { value: "sm", label: "Small",        px: 14 },
  { value: "md", label: "Medium",       px: 16 },
  { value: "lg", label: "Large",        px: 18 },
  { value: "xl", label: "Extra Large",  px: 20 },
];

const CONTRAST_OPTIONS = [
  { value: "standard", label: "Standard" },
  { value: "high",     label: "High" },
];

function AppearanceSettingsCard() {
  const { mode, accent, fontSize, contrast, setMode, setAccent, setFontSize, setContrast } = useAppearance();

  const change = (setter, value, name) => {
    setter(value);
    toast.success(`${name} updated`, { duration: 1200 });
  };

  return (
    <Card data-testid="appearance-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5" /> Appearance
        </CardTitle>
        <CardDescription>
          Personalise the theme, accent color, text size, and contrast. Saves automatically
          and follows you across devices.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {/* THEME MODE */}
        <section data-testid="theme-mode-section">
          <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
            Theme mode
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {THEME_OPTIONS.map((opt) => {
              const active = mode === opt.value;
              const Icon = opt.Icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => change(setMode, opt.value, "Theme")}
                  className={`text-left rounded-xl border p-3 transition-all ${
                    active
                      ? "border-brand-accent ring-2 ring-brand-accent/40 bg-brand-accent-pale"
                      : "border-slate-200 hover:border-slate-400 bg-white"
                  }`}
                  data-testid={`theme-tile-${opt.value}`}
                >
                  <div className={`h-12 rounded-md border ${opt.swatch} flex items-center justify-center mb-2`}>
                    <Icon className="h-5 w-5 text-slate-700" />
                  </div>
                  <p className="text-sm font-medium text-slate-800">{opt.label}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{opt.desc}</p>
                </button>
              );
            })}
          </div>
        </section>

        {/* ACCENT */}
        <section data-testid="accent-section">
          <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
            Accent color
          </h3>
          <div className="flex flex-wrap gap-3">
            {ACCENT_OPTIONS.map((opt) => {
              const active = accent === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => change(setAccent, opt.value, "Accent color")}
                  className={`flex items-center gap-2 rounded-full border p-1.5 pr-3 transition-all ${
                    active ? "border-slate-900 ring-2 ring-slate-900/25" : "border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`accent-swatch-${opt.value}`}
                  aria-label={opt.label}
                >
                  <span
                    className="h-6 w-6 rounded-full border border-white shadow-sm"
                    style={{ backgroundColor: opt.hex }}
                  />
                  <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* FONT SIZE */}
        <section data-testid="font-size-section">
          <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
            Text size
          </h3>
          <div className="flex flex-wrap gap-2">
            {FONT_OPTIONS.map((opt) => {
              const active = fontSize === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => change(setFontSize, opt.value, "Text size")}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-all ${
                    active
                      ? "bg-brand-accent text-white border-brand-accent"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`font-size-${opt.value}`}
                >
                  {opt.label} <span className="text-[10px] opacity-70 ml-1">{opt.px}px</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* CONTRAST */}
        <section data-testid="contrast-section">
          <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
            Interface contrast
          </h3>
          <div className="flex gap-2">
            {CONTRAST_OPTIONS.map((opt) => {
              const active = contrast === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => change(setContrast, opt.value, "Contrast")}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-all ${
                    active
                      ? "bg-brand-accent text-white border-brand-accent"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`contrast-${opt.value}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* LIVE PREVIEW */}
        <section data-testid="live-preview">
          <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
            Live preview
          </h3>
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3 shadow-sm">
            <p className="text-slate-500 text-[11px] uppercase tracking-widest">Sample dashboard card</p>
            <p className="text-slate-900 text-lg font-semibold">$16,344.00</p>
            <p className="text-sm text-slate-500">This is how the interface will look with your current settings.</p>
            <div className="flex items-center gap-3">
              <button className="rounded-md bg-brand-accent text-white text-sm px-3 py-1.5 hover:bg-brand-accent-hover">
                Primary button
              </button>
              <span className="rounded-full bg-brand-accent-pale text-brand-accent px-2 py-0.5 text-[11px] font-medium">
                Accent chip
              </span>
              <span className="text-sm text-slate-500">Muted body copy.</span>
            </div>
            <div className="rounded-md border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-1.5 text-xs text-slate-500 font-medium">Row</th>
                    <th className="text-right px-3 py-1.5 text-xs text-slate-500 font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100">
                    <td className="px-3 py-1.5 text-slate-700">Sample record</td>
                    <td className="px-3 py-1.5 text-right text-slate-900">$1,234.56</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </CardContent>
    </Card>
  );
}

